import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * @param {ParentNode | Element | null} root
   * @returns {string[]}
   */
  #getSlideIds(root) {
    if (!(root instanceof Element)) return [];

    const gallery = root.matches('media-gallery') ? root : root.querySelector('media-gallery');
    if (!(gallery instanceof Element)) return [];

    return [...gallery.querySelectorAll('slideshow-slide[slide-id]')].map((slide) =>
      String(slide.getAttribute('slide-id'))
    );
  }

  /**
   * @param {ParentNode | Element | null} root
   * @returns {Element | null}
   */
  #findSlideshow(root) {
    if (!(root instanceof Element)) return null;

    const gallery = root.matches('media-gallery') ? root : root.querySelector('media-gallery');
    return gallery?.querySelector('slideshow-component') ?? null;
  }

  /**
   * Selects the variant's featured media slide in the gallery.
   * @param {ParentNode | Element | null} root
   * @param {string | number | undefined} featuredMediaId
   * @param {{ animate?: boolean }} [options]
   */
  #selectVariantSlide(root, featuredMediaId, { animate = false } = {}) {
    /** @type {number} */
    let attempts = 0;

    const trySelect = () => {
      const slideshow = this.#findSlideshow(root);

      if (!(slideshow && 'select' in slideshow && typeof slideshow.select === 'function')) {
        if (++attempts < 12) requestAnimationFrame(trySelect);
        return;
      }

      const hasSlides = 'slides' in slideshow && Array.isArray(slideshow.slides) && slideshow.slides.length > 0;

      if (!hasSlides) {
        if (++attempts < 12) requestAnimationFrame(trySelect);
        return;
      }

      if (featuredMediaId) {
        slideshow.select({ id: String(featuredMediaId) }, undefined, { animate });
      } else {
        slideshow.select(0, undefined, { animate });
      }
    };

    requestAnimationFrame(trySelect);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const sectionId = this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    const newMediaGallery =
      (sectionId && source.querySelector(`#shopify-section-${sectionId} media-gallery`)) ||
      source.querySelector('.product-information__media media-gallery') ||
      source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const featuredMediaId = event.detail.resource?.featured_media?.id;
    const currentSlideIds = this.#getSlideIds(this);
    const newSlideIds = this.#getSlideIds(newMediaGallery);
    const slidesUnchanged =
      currentSlideIds.length > 0 &&
      currentSlideIds.length === newSlideIds.length &&
      currentSlideIds.every((id, index) => id === newSlideIds[index]);

    // When only the selected variant changed, the slide list is identical — keep the
    // existing gallery DOM so the shopper's selection is not reset by slideshow re-init.
    if (slidesUnchanged) {
      this.#selectVariantSlide(this, featuredMediaId);
      return;
    }

    const parent = this.parentElement;

    this.replaceWith(newMediaGallery);

    if (!parent) return;

    this.#selectVariantSlide(parent, featuredMediaId);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
